import React, { useState, useEffect, useMemo } from "react";
import { apiFetch } from "../services/api";
import { buildEntityTypeAttributesQueryParams } from "../utils/entityTypeAttributes";
import { createDevConsole } from "../utils/devLogger";

const console = createDevConsole();

const EntityCustomAttributes = ({
  entityForm,
  setEntityForm,
  editingEntity,
  readOnly,
  entityType,
  /** Hide the top “Custom Attributes” title block when embedded in another section shell. */
  suppressIntro = false,
  sectionsIncludeOnly = null,
  sectionsExclude = null,
  omitSectionHeaders = false,
}) => {
  const [attributes, setAttributes] = useState([]);
  // Only show loading initially if we don't have entity_type_id (which would skip lookup)
  const [loading, setLoading] = useState(!editingEntity?.entity_type_id);
  const [customAttributes, setCustomAttributes] = useState({});

  // Load attribute definitions when entity type changes
  useEffect(() => {
    if (entityType) {
      // Use entity_type_id from editingEntity if available (avoids lookup)
      const entityTypeId = editingEntity?.entity_type_id || null;
      // Only set loading if we need to do a lookup (no entity_type_id)
      if (!entityTypeId) {
        setLoading(true);
      }
      loadAttributes(entityType, entityTypeId);
    } else {
      setAttributes([]);
      setLoading(false);
    }
  }, [entityType, editingEntity?.entity_type_id]);

  // Load existing custom_attributes when editing
  useEffect(() => {
    if (editingEntity) {
      let existing = {};

      // Always try to load custom_attributes, even if it's an empty object
      if (
        editingEntity.custom_attributes !== undefined &&
        editingEntity.custom_attributes !== null
      ) {
        try {
          if (typeof editingEntity.custom_attributes === "string") {
            existing = JSON.parse(editingEntity.custom_attributes);
          } else if (typeof editingEntity.custom_attributes === "object") {
            existing = editingEntity.custom_attributes;
          }
        } catch (e) {
          console.error("Error parsing custom_attributes:", e);
          existing = {};
        }
      }

      setCustomAttributes(existing);

      // Also sync to entityForm
      setEntityForm((prev) => ({
        ...prev,
        custom_attributes: existing,
      }));
    } else {
      // Reset for new entities
      setCustomAttributes({});
      setEntityForm((prev) => ({
        ...prev,
        custom_attributes: {},
      }));
    }
  }, [editingEntity?.id, editingEntity?.custom_attributes]);

  // Sync customAttributes state to entityForm whenever it changes
  useEffect(() => {
    setEntityForm((prev) => {
      // Only update if the value actually changed to avoid infinite loops
      if (
        JSON.stringify(prev.custom_attributes || {}) !==
        JSON.stringify(customAttributes || {})
      ) {
        return {
          ...prev,
          custom_attributes: customAttributes,
        };
      }
      return prev;
    });
  }, [customAttributes, setEntityForm]);

  const loadAttributes = async (entityTypeName, entityTypeId = null) => {
    try {
      setLoading(true);

      if (!entityTypeName) {
        console.warn("No entity type name provided");
        setAttributes([]);
        return;
      }

      const param = buildEntityTypeAttributesQueryParams(
        entityTypeName,
        entityTypeId
      );
      if (!param) {
        setAttributes([]);
        return;
      }
      const response = await apiFetch(
        `/api/admin/entity-type-attributes?${param}`
      );
      if (response.ok) {
        const data = await response.json();
        setAttributes(Array.isArray(data) ? data : []);
      } else {
        console.warn("Failed to load custom attributes");
        setAttributes([]);
      }
    } catch (error) {
      console.error("Error loading custom attributes:", error);
      setAttributes([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredAttributes = useMemo(() => {
    let list = attributes;
    if (sectionsIncludeOnly?.length) {
      const allow = new Set(sectionsIncludeOnly);
      list = list.filter((a) => allow.has(a.section || "General"));
    }
    if (sectionsExclude?.length) {
      const deny = new Set(sectionsExclude);
      list = list.filter((a) => !deny.has(a.section || "General"));
    }
    return list;
  }, [attributes, sectionsIncludeOnly, sectionsExclude]);

  const { groupedAttributes, sortedSections } = useMemo(() => {
    const groups = filteredAttributes.reduce((acc, attr) => {
      const section = attr.section || "General";
      if (!acc[section]) {
        acc[section] = [];
      }
      acc[section].push(attr);
      return acc;
    }, {});
    const sorted = Object.keys(groups).sort();
    sorted.forEach((section) => {
      groups[section].sort(
        (a, b) =>
          a.sort_order - b.sort_order ||
          a.attribute_label.localeCompare(b.attribute_label)
      );
    });
    return { groupedAttributes: groups, sortedSections: sorted };
  }, [filteredAttributes]);

  const handleAttributeChange = (attributeName, value) => {
    setCustomAttributes((prev) => {
      const updated = { ...prev };
      // Handle empty values - remove the key if empty
      if (
        value === "" ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
      ) {
        delete updated[attributeName];
      } else {
        updated[attributeName] = value;
      }
      // Immediately sync to entityForm
      setEntityForm((prevForm) => ({
        ...prevForm,
        custom_attributes: updated,
      }));
      return updated;
    });
  };

  const renderAttributeField = (attr) => {
    const currentValue = customAttributes[attr.attribute_name];
    const fieldId = `attr_${attr.attribute_name}`;
    const isRequired = attr.is_required;
    const baseClasses = `w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
      readOnly ? "opacity-50 cursor-not-allowed" : ""
    } ${isRequired ? "border-l-4 border-l-yellow-500" : ""}`;

    switch (attr.data_type) {
      case "text":
        return (
          <input
            key={fieldId}
            id={fieldId}
            type="text"
            value={currentValue || ""}
            onChange={(e) =>
              handleAttributeChange(attr.attribute_name, e.target.value)
            }
            disabled={readOnly}
            required={isRequired}
            placeholder={
              attr.description || `Enter ${attr.attribute_label.toLowerCase()}`
            }
            className={baseClasses}
          />
        );

      case "number":
        const validationRules = attr.validation_rules || {};
        const min = validationRules.min;
        const max = validationRules.max;
        return (
          <input
            key={fieldId}
            id={fieldId}
            type="number"
            value={currentValue ?? ""}
            onChange={(e) => {
              const val =
                e.target.value === "" ? null : parseFloat(e.target.value);
              handleAttributeChange(attr.attribute_name, val);
            }}
            disabled={readOnly}
            required={isRequired}
            min={min}
            max={max}
            step={validationRules.step || "any"}
            placeholder={
              attr.description || `Enter ${attr.attribute_label.toLowerCase()}`
            }
            className={baseClasses}
          />
        );

      case "boolean":
        return (
          <label
            key={fieldId}
            className="flex items-center gap-3 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={currentValue === true || currentValue === "true"}
              onChange={(e) =>
                handleAttributeChange(attr.attribute_name, e.target.checked)
              }
              disabled={readOnly}
              className="w-5 h-5 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
            />
            <span className="text-gray-300">
              {attr.description ||
                `Enable ${attr.attribute_label.toLowerCase()}`}
            </span>
          </label>
        );

      case "date":
        return (
          <input
            key={fieldId}
            id={fieldId}
            type="date"
            value={
              currentValue
                ? currentValue.includes("T")
                  ? currentValue.split("T")[0]
                  : currentValue
                : ""
            }
            onChange={(e) =>
              handleAttributeChange(attr.attribute_name, e.target.value || null)
            }
            disabled={readOnly}
            required={isRequired}
            className={baseClasses}
          />
        );

      case "select":
        const options = attr.validation_rules?.options || [];
        return (
          <select
            key={fieldId}
            id={fieldId}
            value={currentValue || ""}
            onChange={(e) =>
              handleAttributeChange(attr.attribute_name, e.target.value || null)
            }
            disabled={readOnly}
            required={isRequired}
            className={baseClasses}
          >
            <option value="">-- Select {attr.attribute_label} --</option>
            {options.map((option, idx) => (
              <option key={idx} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case "multi_select":
        const multiOptions = attr.validation_rules?.options || [];
        const selectedValues = Array.isArray(currentValue)
          ? currentValue
          : currentValue
            ? [currentValue]
            : [];
        return (
          <div key={fieldId} className="space-y-2">
            {multiOptions.map((option, idx) => (
              <label
                key={idx}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option)}
                  onChange={(e) => {
                    let newValues;
                    if (e.target.checked) {
                      newValues = [...selectedValues, option];
                    } else {
                      newValues = selectedValues.filter((v) => v !== option);
                    }
                    handleAttributeChange(
                      attr.attribute_name,
                      newValues.length > 0 ? newValues : null
                    );
                  }}
                  disabled={readOnly}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                />
                <span className="text-gray-300">{option}</span>
              </label>
            ))}
          </div>
        );

      default:
        return (
          <input
            key={fieldId}
            id={fieldId}
            type="text"
            value={currentValue || ""}
            onChange={(e) =>
              handleAttributeChange(attr.attribute_name, e.target.value)
            }
            disabled={readOnly}
            className={baseClasses}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {!suppressIntro ? (
          <div>
            <h2 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">
              📋 Custom Attributes
            </h2>
          </div>
        ) : null}
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (attributes.length === 0) {
    return null; // Don't show section if no attributes defined
  }

  if (
    attributes.length > 0 &&
    sortedSections.length === 0 &&
    (sectionsIncludeOnly?.length || sectionsExclude?.length)
  ) {
    return null;
  }

  return (
    <div className="space-y-6">
      {!suppressIntro ? (
        <div>
          <h2 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">
            📋 Custom Attributes
          </h2>
          <p className="text-sm text-gray-400 mt-2">
            Additional attributes specific to this entity type
          </p>
        </div>
      ) : null}

      {sortedSections.length > 0 ? (
        <div className="space-y-6">
          {sortedSections.map((section) => (
            <div
              key={section}
              className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700"
            >
              {/* Section Header */}
              {section !== "General" && !omitSectionHeaders && (
                <div className="bg-gray-750 px-4 py-2 border-b border-gray-700">
                  <h3 className="text-base font-medium text-gray-200">
                    {section}
                  </h3>
                </div>
              )}

              {/* Section Fields */}
              <div className="p-4 space-y-4">
                {groupedAttributes[section].map((attr) => (
                  <div key={attr.id}>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {attr.attribute_label}
                      {attr.is_required && (
                        <span className="text-red-400 ml-1">*</span>
                      )}
                    </label>
                    {renderAttributeField(attr)}
                    {attr.description && (
                      <p className="text-xs text-gray-400 mt-1">
                        {attr.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <p className="text-gray-400 text-sm">
            No custom attributes configured for this entity type.
          </p>
        </div>
      )}
    </div>
  );
};

export default EntityCustomAttributes;
